import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL, authFetch } from '../../services/api';
import StandardsProgressionDropdowns from '../Admin/StandardsProgressionDropdowns';

export default function StandardProgressionsReference() {
  const { token, logout } = useAuth();
  const { state } = useLocation();
  const navigate = useNavigate();
  const restoredState = state?.progressionState || {};
  const [standards, setStandards] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [, setFilters] = useState({ gradeLevels: [], subject: '', domain: '', standards: [] });

  useEffect(() => {
    async function fetchStandards() {
      const endpoints = [
        `${API_URL}/api/progression/standards-progression-data`,
        `${API_URL}/api/teacher/standards-progression-data`,
      ];

      try {
        setLoading(true);
        setError('');
        let lastError = 'Failed to fetch standard progression data';
        let networkFailureCount = 0;

        for (const endpoint of endpoints) {
          let response;
          try {
            response = await authFetch(`${endpoint}?ts=${Date.now()}`, {
              headers: { 'Content-Type': 'application/json' },
            });
          } catch (fetchErr: any) {
            networkFailureCount += 1;
            console.error('[StandardProgressionsReference] Network error while loading endpoint:', endpoint, fetchErr);
            continue;
          }

          if (response.status === 401) {
            logout();
            navigate('/login');
            return;
          }

          if (!response.ok) {
            lastError = `Request failed (${response.status})`;
            continue;
          }

          const text = await response.text();
          let data;
          try {
            data = JSON.parse(text);
          } catch (_err: any) {
            continue;
          }

          const rows = Array.isArray(data)
            ? data
            : Array.isArray(data?.standards)
              ? data.standards
              : Array.isArray(data?.data)
                ? data.data
                : [];

          setStandards(rows);
          return;
        }

        if (networkFailureCount === endpoints.length) {
          throw new Error('Failed to fetch standards data from the API. Please retry and check backend connectivity.');
        }

        throw new Error(lastError);
      } catch (err: any) {
        setError(err.message || 'Could not load standard progression data.');
      } finally {
        setLoading(false);
      }
    }

    if (!token) {
      setError('Teacher session not found. Please log in again.');
      setLoading(false);
      return;
    }

    fetchStandards();
  }, [token, logout, navigate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 1152, background: 'white', borderRadius: 14, boxShadow: '0 2px 8px #0001', border: '1px solid #e0e7ef', padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
          <StandardsProgressionDropdowns
            data={standards}
            onChange={setFilters}
            title="Standard Progressions"
            allowCellInteraction={false}
            initialGrade={restoredState.grade || ''}
            initialSubject={restoredState.subject || ''}
            initialDomain={restoredState.domain || ''}
            initialStrand={restoredState.strand || ''}
            compact={true}
            enableContinueFlow={false}
          />
        </div>
        {loading && <div style={{ color: '#6b7280', fontSize: 15 }}>Loading...</div>}
        {error && standards.length === 0 && <div style={{ color: '#b91c1c', fontWeight: 600, fontSize: 15 }}>{error}</div>}
        {!loading && !error && standards.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 15 }}>No standard progression data is available for this teacher reference view.</div>
        )}
      </div>
    </div>
  );
}